import { createSlice } from '@reduxjs/toolkit';
import { fetchWorkspacePermissions } from '../thunks/workspacePermissionsThunks';

/**
 * Tenant-effective permission codes for the ACTIVE workspace.
 *
 * Spec: edi-role-user-counts-and-admin-assignment (capability
 * tenant-scoped-permission-resolution — "Tenant-effective permissions are
 * exposed to the client").
 *
 * The login-time permission list is built from GLOBAL roles only, so a role
 * granted per-tenant (sm_core_sysuser_tenants — e.g. edi.admin) enforces on the
 * backend but drives no FE affordances. This slice holds the backend's actual
 * effective set for the current workspace (`GET /me/permissions?tenantId=` —
 * the Phase-2 global ∪ current-tenant-role union), and
 * `selectCurrentUserPermissions` unions it with the login list.
 *
 * NOT to be confused with the legacy `auth.tenantPermissions` field, which is a
 * misleadingly-named structured mirror of the same GLOBAL list — its lifecycle
 * is left untouched (see design.md D3).
 *
 * Lifecycle: fetched when a workspace becomes active (AppShell effect keyed on
 * the active tenant id), overwritten on tenant switch, cleared on logout and on
 * fetch failure. `tenantId` records which tenant the set belongs to so the
 * selector can drop a stale set during a switch race.
 */
export interface WorkspacePermissionsState {
  /** The tenant the loaded set belongs to — null when nothing is loaded. */
  tenantId: number | null;
  /** Backend-resolved effective permission codes for that tenant. */
  permissions: string[];
  isLoading: boolean;
}

const initialState: WorkspacePermissionsState = {
  tenantId: null,
  permissions: [],
  isLoading: false,
};

const workspacePermissionsSlice = createSlice({
  name: 'workspacePermissions',
  initialState,
  reducers: {
    clearWorkspacePermissions: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspacePermissions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchWorkspacePermissions.fulfilled, (state, action) => {
        state.tenantId = action.payload.tenantId;
        state.permissions = action.payload.permissions;
        state.isLoading = false;
      })
      .addCase(fetchWorkspacePermissions.rejected, () => initialState)
      // A signed-out user has no workspace context — drop the set.
      .addCase('auth/logoutSuccess', () => initialState);
  },
});

export const { clearWorkspacePermissions } = workspacePermissionsSlice.actions;
export default workspacePermissionsSlice.reducer;
